import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import categoryService from "../../services/categoryService"; // मान लिया गया कि यह फ़ाइल मौजूद है

// ----------------------------------------------------------------------
// ASYNC THUNKS (Exported on declaration)
// ----------------------------------------------------------------------

export const fetchCategories = createAsyncThunk(
  "categories/fetchCategories",
  async (params, { rejectWithValue }) => {
    try {
      const response = await categoryService.getAllCategories(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch categories"
      );
    }
  }
);

export const fetchCategoryById = createAsyncThunk(
  "categories/fetchCategoryById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await categoryService.getCategoryById(id);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch category"
      );
    }
  }
);

export const createCategory = createAsyncThunk(
  "categories/createCategory",
  async ({ categoryData, imageFile }, { rejectWithValue }) => {
    try {
      const response = await categoryService.createCategory(
        categoryData,
        imageFile
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create category"
      );
    }
  }
);

export const updateCategory = createAsyncThunk(
  "categories/updateCategory",
  async ({ id, categoryData, imageFile }, { rejectWithValue }) => {
    try {
      const response = await categoryService.updateCategory(
        id,
        categoryData,
        imageFile
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update category"
      );
    }
  }
);

export const deleteCategory = createAsyncThunk(
  "categories/deleteCategory",
  async (id, { rejectWithValue }) => {
    try {
      const response = await categoryService.deleteCategory(id);
      return { id, response };
    } catch (error) {
      // Return error message from backend
      const errorData = error.response?.data || {};
      return rejectWithValue(
        errorData.message || "Failed to delete category"
      );
    }
  }
);

export const updateCategoryStatus = createAsyncThunk(
  "categories/updateCategoryStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await categoryService.updateCategoryStatus(id, status);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update category status"
      );
    }
  }
);

export const fetchCategoryStats = createAsyncThunk(
  "categories/fetchCategoryStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await categoryService.getCategoryStats();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch category stats"
      );
    }
  }
);

// ----------------------------------------------------------------------
// SLICE DEFINITION
// ----------------------------------------------------------------------

const categorySlice = createSlice({
  name: "categories",
  initialState: {
    categories: [],
    currentCategory: null,
    stats: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
    },
    filters: {
      status: "",
      name: "",
    },
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
    success: null,
  },
  reducers: {
    // Sync Actions
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: "",
        name: "",
      };
    },
    setCurrentCategory: (state, action) => {
      state.currentCategory = action.payload;
    },
    clearCurrentCategory: (state) => {
      state.currentCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload.data || action.payload;
        const data = responseData.categories || responseData;
        state.categories = Array.isArray(data) ? data : [];
        state.pagination = responseData.pagination || {
          page: 1,
          limit: 10,
          total: Array.isArray(data) ? data.length : 0,
        };
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch category by ID
      .addCase(fetchCategoryById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCategoryById.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload.data || action.payload;
        state.currentCategory = responseData.category || responseData;
      })
      .addCase(fetchCategoryById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create category (Re-render Fix: Using push to maintain array reference for minimal re-renders)
      .addCase(createCategory.pending, (state) => {
        state.isCreating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.isCreating = false;
        const responseData = action.payload.data || action.payload;
        const newCategory = responseData.category || responseData;
        if (newCategory) {
          // ✅ .push() maintains array reference unless array size changes significantly
          state.categories.push(newCategory);
        }
        state.success =
          action.payload.message || "Category created successfully";
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })

      // Update category (Fix: Updates item in place)
      .addCase(updateCategory.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.isUpdating = false;
        const responseData = action.payload.data || action.payload;
        const updatedCategory = responseData.category || responseData;
        const index = state.categories.findIndex(
          (category) => category.id === updatedCategory.id
        );
        if (index !== -1) {
          state.categories[index] = updatedCategory;
        }
        if (state.currentCategory?.id === updatedCategory.id) {
          state.currentCategory = updatedCategory;
        }
        state.success =
          action.payload.message || "Category updated successfully";
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // Delete category (Fix: Filters out deleted item)
      .addCase(deleteCategory.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.isDeleting = false;
        // Filter creates a new array reference, which is expected for deletion
        state.categories = state.categories.filter(
          (category) => category.id !== action.payload.id
        );
        state.success =
          action.payload.response.message || "Category deleted successfully";
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      })

      // Update category status
      .addCase(updateCategoryStatus.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateCategoryStatus.fulfilled, (state, action) => {
        state.isUpdating = false;
        const responseData = action.payload.data || action.payload;
        const updatedCategory = responseData.category || responseData;
        const index = state.categories.findIndex(
          (category) => category.id === updatedCategory.id
        );
        if (index !== -1) {
          state.categories[index] = updatedCategory;
        }
        if (state.currentCategory?.id === updatedCategory.id) {
          state.currentCategory = updatedCategory;
        }
        state.success =
          action.payload.message || "Category status updated successfully";
      })
      .addCase(updateCategoryStatus.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // Fetch category stats
      .addCase(fetchCategoryStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCategoryStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload.data || action.payload;
      })
      .addCase(fetchCategoryStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// ----------------------------------------------------------------------
// EXPORTS (Sync actions and Default reducer)
// ----------------------------------------------------------------------

// Export sync actions
export const {
  clearError,
  clearSuccess,
  setFilters,
  clearFilters,
  setCurrentCategory,
  clearCurrentCategory,
} = categorySlice.actions;

// Export reducer (default)
export default categorySlice.reducer;
